import { queryClient } from '@/data';
import { SbkBetSlipState } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import {
    Selection,
    SgpBetError,
    sgpConflictingErrorCodeSchema,
    sgpDisabledErrorCodeSchema,
} from '@/feature/betslip-sbk/types';
import { sgpTemporaryErrorCodeSchema } from '@/feature/betslip-sbk/types';
import { sgpOddsKeys } from '@/feature/betslip-sbk/utils/betslip-actions';
import { generateSgpOddsId, isComboSelectionEnabled } from '@/feature/betslip-sbk/utils/betslip-utils';
import { getSgpOdds } from '@/feature/betslip-sbk/utils/get-sgp-odds';
import { CancelledError } from '@tanstack/react-query';

/**
 * Fetches SGP odds using react query
 * @param eventId - event id
 * @param selections - array of selections
 * @param forceUpdate - boolean to force update the cache
 */
export const fetchSgpOdds = async (
    eventId: string,
    selections: Selection[],
    forceUpdate: boolean = false,
    onFetching: () => void
) => {
    const payload = {
        event_id: eventId,
        bets: selections.map(selection => {
            return {
                side_bet_id: selection.marketId,
                side_bet_option_id: selection.optionId,
            };
        }),
    };
    await queryClient.cancelQueries({ queryKey: sgpOddsKeys.sgpOdds(payload) });
    if (forceUpdate) {
        await queryClient.invalidateQueries({ queryKey: sgpOddsKeys.sgpOdds(payload) });
    }
    const resp = await queryClient.fetchQuery({
        queryKey: sgpOddsKeys.sgpOdds(payload),
        queryFn: () => getSgpOdds(payload, onFetching),
        staleTime: 30 * 1000, // Cache for 30 seconds
        retry: (failureCount, err) => {
            if (err instanceof Error && sgpTemporaryErrorCodeSchema.safeParse(err.message).success) {
                return failureCount < 3;
            }
            return false;
        }, // Retry 3 times if it is temporary issue
        retryDelay: 1000,
    });
    return resp.data.odds;
};

/**
 * Returns an array of selections for valid SGP combinations
 * ex. [[selection1, selection2], [selection3, selection4, selection5]]
 * @param state - SbkBetSlip
 * @param filterEventId - optional event id to filter
 */
export const getSgpCombinations = (
    state: Pick<SbkBetSlipState, 'selections' | 'options' | 'producerStatuses' | 'markets'>,
    filterEventId?: string
) => {
    const selections = Object.values(state.selections);

    const eventIds = filterEventId
        ? [filterEventId]
        : Array.from(new Set(selections.map(selection => selection.eventId)));

    return eventIds
        .map(id =>
            selections.filter(selection => selection.eventId === id && isComboSelectionEnabled(selection, state))
        )
        .filter(s => s.length > 1);
};

/**
 * Handles SGP service down error
 * If the SGP service is down, it will default to the first selection toggled on, while
 * the rest will be toggled off.
 */
export const handleSgpServiceDownError = (
    state: Pick<SbkBetSlipState, 'selectionOrder' | 'selections'>,
    selectionIds: string[]
): Pick<SbkBetSlipState, 'selectionOrder' | 'selections'> => {
    // order selections by selectionOrder
    const orderedSelectionIds = selectionIds.sort(
        (a, b) => state.selectionOrder.indexOf(a) - state.selectionOrder.indexOf(b)
    );
    const selections = orderedSelectionIds.reduce((acc, id, index) => {
        return {
            ...acc,
            [id]: {
                ...state.selections[id],
                isComboEnabled: index === 0,
            },
        };
    }, {});

    return {
        ...state,
        selections: {
            ...state.selections,
            ...selections,
        },
    };
};

/**
 * Toggle off all selections for the event that has SGP temporarily goes down.
 */
export const handleSgpToggleOff = (
    state: Pick<SbkBetSlipState, 'selections'>,
    eventId: string
): Pick<SbkBetSlipState, 'selections'> => {
    const selections = Object.values(state.selections).reduce((acc, s) => {
        if (s.eventId === eventId) {
            return {
                ...acc,
                [s.id]: {
                    ...s,
                    isComboEnabled: false,
                },
            };
        }
        return acc;
    }, {});

    return {
        ...state,
        selections: {
            ...state.selections,
            ...selections,
        },
    };
};

type SgpStateHandler = Pick<
    SbkBetSlipState,
    | 'sgpOdds'
    | 'sgpEventDisabled'
    | 'sgpTemporaryIssue'
    | 'selectionOrder'
    | 'selections'
    | 'oddsChangeTimeout'
    | 'sgpOddsChanges'
>;

/**
 * Updates state when SGP odds are successfully fetched
 */
export const handleSgpSuccess = (sgpId: string, odds: number, eventId: string, state: SgpStateHandler) => {
    return {
        ...state,
        sgpOdds: {
            ...state.sgpOdds,
            [sgpId]: odds,
        },
        sgpEventDisabled: {
            ...state.sgpEventDisabled,
            [eventId]: false,
        },
    };
};

/**
 * Returns the partial state updates for an SGP error without requiring the full state.
 * Returns null if the error is a cancelled request.
 */
export const getSgpErrorUpdates = (
    err: unknown,
    sgpId: string,
    eventId: string,
    selections: Selection[],
    selectionOrder: string[]
): Partial<SgpStateHandler> | null => {
    // cancelled request
    if (err instanceof CancelledError && err.revert) {
        return null;
    }
    const selectionsMap = selections.reduce<Record<string, Selection>>((acc, s) => {
        acc[s.id] = s;
        return acc;
    }, {});

    // conflicting selections — disable the newest selection, enable the rest
    if (err instanceof SgpBetError && sgpConflictingErrorCodeSchema.safeParse(err.code).success) {
        const newestSelectionId = [...selections].sort(
            (a, b) => selectionOrder.indexOf(b.id) - selectionOrder.indexOf(a.id)
        )[0].id;

        return {
            sgpOdds: { [sgpId]: false },
            selections: selections.reduce<Record<string, Selection>>((acc, s) => {
                acc[s.id] = { ...s, isComboEnabled: s.id !== newestSelectionId };
                return acc;
            }, {}),
        };
    }
    const state = { selections: selectionsMap, selectionOrder };

    // SGP service down — keep only the first selection enabled
    if (err instanceof SgpBetError && sgpDisabledErrorCodeSchema.safeParse(err.code).success) {
        return {
            ...handleSgpServiceDownError(
                state,
                selections.map(s => s.id)
            ),
            sgpEventDisabled: { [eventId]: true },
        };
    }
    // Temporary issue — disable all selections
    return {
        ...handleSgpToggleOff(state, eventId),
        sgpTemporaryIssue: { [eventId]: true },
    };
};

/**
 * Updates state when SGP fail to fetch and handles the different error scenarios
 */
export const handleSgpError = (
    err: unknown,
    sgpId: string,
    eventId: string,
    selections: Selection[],
    state: SgpStateHandler
): SgpStateHandler => {
    const updates = getSgpErrorUpdates(err, sgpId, eventId, selections, state.selectionOrder);
    if (!updates) {
        return state;
    }
    return {
        ...state,
        ...updates,
        sgpOdds: { ...state.sgpOdds, ...updates.sgpOdds },
        sgpEventDisabled: { ...state.sgpEventDisabled, ...updates.sgpEventDisabled },
        sgpTemporaryIssue: { ...state.sgpTemporaryIssue, ...updates.sgpTemporaryIssue },
        selections: { ...state.selections, ...updates.selections },
    };
};

/**
 * After a selection is removed, re-validates all remaining selections for the event
 * by fetching SGP odds for the full combination (enabled + disabled).
 * Does NOT modify state -- returns the updates to apply.
 */
export const revalidateSelectionsAfterRemoval = async (
    eventId: string,
    allSelectionsForEvent: Selection[],
    selectionOrder: string[],
    onFetching: () => void
): Promise<Partial<SgpStateHandler> | null> => {
    if (allSelectionsForEvent.length < 2) {
        return null;
    }

    const sgpId = generateSgpOddsId(
        allSelectionsForEvent.map(s => s.id),
        eventId
    );

    try {
        const odds = await fetchSgpOdds(eventId, allSelectionsForEvent, true, onFetching);
        // Success: all selections are compatible — enable all and store odds
        const enabledSelections = allSelectionsForEvent.reduce<Record<string, Selection>>((acc, s) => {
            acc[s.id] = { ...s, isComboEnabled: true };
            return acc;
        }, {});
        return {
            sgpOdds: { [sgpId]: odds },
            sgpEventDisabled: { [eventId]: false },
            sgpTemporaryIssue: { [eventId]: false },
            selections: enabledSelections,
        };
    } catch (err) {
        // For conflicting errors, disable all remaining selections since we can't
        // determine which specific pair conflicts after a removal
        if (err instanceof SgpBetError && sgpConflictingErrorCodeSchema.safeParse(err.code).success) {
            return {
                sgpOdds: { [sgpId]: false },
                selections: allSelectionsForEvent.reduce<Record<string, Selection>>((acc, s) => {
                    acc[s.id] = { ...s, isComboEnabled: false };
                    return acc;
                }, {}),
            };
        }
        return getSgpErrorUpdates(err, sgpId, eventId, allSelectionsForEvent, selectionOrder);
    }
};

/**
 * Checks if any of the fetched SGP odds have conflicting selection error
 */
export const hasConflictingSelections = (results: PromiseSettledResult<number>[]) => {
    return results.some(
        result =>
            result.status === 'rejected' &&
            result.reason instanceof SgpBetError &&
            sgpConflictingErrorCodeSchema.safeParse(result.reason.code).success
    );
};
